import MarkdownComponent from 'components/shared/MarkdownComponent';
import WIPFeaturesContext from 'context/wip-features/wip-features.context';
import { useI18n } from 'hooks/useI18n';
import { UIOfferHeroSection } from 'interfaces/ui/ui-offer-hero-section';
import { useRouter } from 'next/dist/client/router';
import { useContext } from 'react';
import Avatar from '../../shared/Avatar';
import { Badge } from '../../shared/Badge';
import { Heading1 } from '../../shared/Typography';
import {
  StyledHeroClosedBadge,
  StyledHeroInfo,
  StyledHeroInfoBg,
  StyledHeroInfoCompany,
  StyledHeroInfoCompanyArea,
  StyledHeroInfoCompanyLogo,
  StyledHeroInfoContainer,
  StyledHeroInfoDescription,
  StyledHeroInfoFooter,
  StyledHeroInfoManagement,
  StyledHeroInfoScout,
  StyledHeroInfoScoutInfo,
  StyledHeroLanguageBadge
} from './HeroInfo.styled';
import HeroInfoData from './HeroInfoData';

type HeroInfoProps = UIOfferHeroSection & {
  onlyAvailableInLang: string | null;
};

const HeroInfo: React.FC<HeroInfoProps> = (props) => {
  const { t } = useI18n('job-offer');
  const { locale } = useRouter();
  const showWIPFeatures = useContext(WIPFeaturesContext);

  return (
    <StyledHeroInfo>
      <StyledHeroInfoContainer>
        <StyledHeroInfoBg>
          <div></div>
        </StyledHeroInfoBg>

        <StyledHeroInfoCompany>
          <StyledHeroInfoCompanyLogo
            src={props.companyLogoDark}
            alt={props.companyName}
          />

          {showWIPFeatures && props.companyArea && (
            <StyledHeroInfoCompanyArea>
              {props.companyArea}
            </StyledHeroInfoCompanyArea>
          )}
        </StyledHeroInfoCompany>

        {props.onlyAvailableInLang &&
          locale?.toLowerCase() !== props.onlyAvailableInLang.toLowerCase() && (
            <StyledHeroLanguageBadge>
              <span>{t(`hero.onlyAvailable${props.onlyAvailableInLang}`)}</span>
            </StyledHeroLanguageBadge>
          )}

        {props.closed && (
          <StyledHeroClosedBadge>
            <Badge variant="negative">
              {}
              {props.closedDate
                ? t('hero.offerClosed', {
                    date: new Intl.DateTimeFormat(locale, {
                      month: '2-digit',
                      day: '2-digit',
                      year: 'numeric'
                    }).format(new Date(props.closedDate || ''))
                  })
                : t('hero.offerClosedWithoutDate')}
            </Badge>
          </StyledHeroClosedBadge>
        )}

        <Heading1>{props.jobTitle}</Heading1>

        <HeroInfoData {...props} />
      </StyledHeroInfoContainer>

      <StyledHeroInfoDescription>
        {props.migratedToMarkdown ? (
          <>
            <MarkdownComponent content={props.jobDescription || ''} />
          </>
        ) : (
          <div
            dangerouslySetInnerHTML={{ __html: props.jobDescription || '' }}
          ></div>
        )}
      </StyledHeroInfoDescription>

      <StyledHeroInfoFooter>
        <StyledHeroInfoManagement>
          <span>{t('hero.offerHandledBy')}</span>
          <img
            width={110}
            height={20}
            src={props.companyManagementLogoDark}
            alt={props.companyManagementLogoAlt}
          />
        </StyledHeroInfoManagement>
        {props.companyScout !== '' && (
          <StyledHeroInfoScout>
            {props.companyScoutAvatar && (
              <Avatar
                className="scout-avatar"
                avatarImg={props.companyScoutAvatar}
                avatarAlt={props.companyScout}
              />
            )}

            <StyledHeroInfoScoutInfo>
              <span>{t('hero.scout')}</span>
              <a
                href={`mailto:${props.companyScoutUrl}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {props.companyScout}
              </a>
            </StyledHeroInfoScoutInfo>
          </StyledHeroInfoScout>
        )}
      </StyledHeroInfoFooter>
    </StyledHeroInfo>
  );
};

export default HeroInfo;
